import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faChevronDown,
  faChartLine,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { MdBarChart } from "react-icons/md";
import { LuMaximize2, LuMinimize2, LuShare2 } from "react-icons/lu";
import SymbolSearchModal from "./SymbolSearchModal";
import { pairSymbol } from "../../../utils/instrumentFormat";
import {
  getSidebarCollapseOverride,
  subscribeSidebarCollapse,
  toggleSidebarCollapseOverride,
} from "../../../utils/sidebarCollapse";

const DesktopChartToolbar = ({
  selectedStock,
  setting,
  periods = [],
  onOpenChartType,
  onOpenOverlays,
  onOpenIndicators,
  onAlert,
  onShare,
  onStockSelect,
  onPeriodChange,
}) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [timeframeOpen, setTimeframeOpen] = useState(false);

  const currentPeriod = periods.find((p) => p.id === setting?.periodID);
  const periodLabel = currentPeriod?.display || "1D";

  const [collapsed, setCollapsed] = useState(() => {
    const o = getSidebarCollapseOverride();
    return o === null ? false : o;
  });
  useEffect(() => {
    const read = () => {
      const o = getSidebarCollapseOverride();
      setCollapsed(o === null ? false : o);
    };
    read();
    return subscribeSidebarCollapse(read);
  }, []);

  return (
    <div className="flex items-center gap-3 bg-white pr-2 pl-0">
      <button
        type="button"
        onClick={() => toggleSidebarCollapseOverride(false)}
        className="w-10 h-10 flex items-center justify-center rounded hover:bg-gray-100 text-gray-700 border-r border-gray-200"
        title={collapsed ? "Exit full screen" : "Full screen chart"}
      >
        {collapsed ? <LuMinimize2 size={18} /> : <LuMaximize2 size={18} />}
      </button>

      <div className="flex items-center gap-2" id="chart-guide-asset-wheel">
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gray-100 text-gray-800 font-semibold text-sm hover:bg-gray-200"
          title="Change symbol"
        >
          {pairSymbol(selectedStock || setting?.symbol) || "SPY"}
          <FontAwesomeIcon
            icon={faChevronDown}
            className="text-[10px] text-gray-500"
          />
        </button>
      </div>

      <div className="relative" id="chart-guide-timeframe-wheel">
        <button
          type="button"
          onClick={() => setTimeframeOpen((v) => !v)}
          className="min-w-[40px] h-9 px-2 flex items-center justify-center rounded border border-gray-200 hover:bg-gray-50 text-gray-700"
          title="Timeframe"
        >
          <span className="text-sm font-semibold">{periodLabel}</span>
        </button>
        {timeframeOpen && (
          <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 min-w-[100px]">
            {periods.map((p) => {
              const isActive = p.id === setting?.periodID;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    onPeriodChange?.(p);
                    setTimeframeOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                    isActive
                      ? "text-primaryColor font-semibold"
                      : "text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  {p.display}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onOpenChartType}
        className="w-9 h-9 flex items-center justify-center rounded border border-gray-200 hover:bg-gray-50 text-gray-700"
        title="Chart type"
      >
        <MdBarChart size={18} />
      </button>

      <button
        type="button"
        onClick={onOpenOverlays}
        className="px-3 h-9 flex items-center gap-2 rounded border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm"
        title="Overlays"
      >
        <FontAwesomeIcon icon={faPlus} className="text-xs" />
        Overlays
      </button>

      <button
        type="button"
        onClick={onOpenIndicators}
        className="px-3 h-9 flex items-center gap-2 rounded border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm"
        title="Indicators"
      >
        <FontAwesomeIcon icon={faChartLine} />
        Indicators
      </button>

      {/* Alert */}
      <button
        type="button"
        onClick={onAlert}
        className="px-3 h-9 flex items-center gap-2 rounded border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm"
        title="Create alert"
      >
        <FontAwesomeIcon icon={faClock} />
        Alert
      </button>

      <div className="flex-1" />

      <button
        type="button"
        onClick={onShare}
        className="w-10 h-10 flex items-center justify-center rounded text-gray-700 hover:bg-[#F5F7F7]"
        title="Share chart to feed"
        id="chart-guide-share"
      >
        <LuShare2 size={18} />
      </button>

      <SymbolSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onPick={(pick) => onStockSelect?.(pick)}
      />
    </div>
  );
};

export default DesktopChartToolbar;
